import { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { wordpressAPI } from '../services/wordpressApi';
import {
  fetchRecentResources,
  recordResourceView,
  type RecentResourceView,
} from '../services/recentResourcesApi';
import { WPPost } from '../types/wordpress';
import { getRecentlyViewed } from '../utils/recentlyViewed';

async function postsForViewIds(ids: number[]): Promise<WPPost[]> {
  if (ids.length === 0) return [];
  const loaded = await wordpressAPI.fetchPosts({
    include: ids.join(','),
    per_page: ids.length,
  });
  const byId = new Map(loaded.map((post) => [post.id, post]));
  return ids.flatMap((id) => {
    const post = byId.get(id);
    return post ? [post] : [];
  });
}

/** Upload browser-only views that are not yet on the account (oldest first). */
async function mergeLocalViewsIntoAccount(
  getToken: () => Promise<string | null>,
  serverViews: RecentResourceView[]
): Promise<RecentResourceView[]> {
  const local = getRecentlyViewed();
  if (local.length === 0) return serverViews;

  const serverIds = new Set(serverViews.map((view) => view.postId));
  const missing = local.filter((post) => !serverIds.has(post.id));
  if (missing.length === 0) return serverViews;

  for (const post of [...missing].reverse()) {
    await recordResourceView(getToken, post.id);
  }
  return fetchRecentResources(getToken);
}

export function useRecentResources(limit: number): WPPost[] {
  const { getToken, isSignedIn } = useAuth();
  const [posts, setPosts] = useState<WPPost[]>([]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!isSignedIn) {
        if (!cancelled) setPosts(getRecentlyViewed().slice(0, limit));
        return;
      }

      try {
        let views = await fetchRecentResources(getToken);
        views = await mergeLocalViewsIntoAccount(getToken, views);
        const loaded = await postsForViewIds(views.slice(0, limit).map((view) => view.postId));
        if (!cancelled) setPosts(loaded);
      } catch {
        if (!cancelled) setPosts(getRecentlyViewed().slice(0, limit));
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [getToken, isSignedIn, limit]);

  return posts;
}
